import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import { and } from 'ember-truth-helpers';
import type { RoomsTableRow } from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/tab-open-pricing';
import assignTextSuccessCriticalClass from 'lumen/modifiers/assign-text-success-critical-class';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  NUMBER_INPUT_MAX_VALUE,
  RECOMMENDATION_RATE_DIFFERENCE_FORMATS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { formatDifferenceBetweenRecommendationAndOwnRate } from 'lumen/utils/pricing-assistant/pricing-assistant-formatting';

interface HotelPricingAssistantTableCellsTdPriceNewSignature {
  Args: {
    column: {
      selectedRows: RoomsTableRow[];
    };
    row: RoomsTableRow;
    value: {
      autofocus: boolean;
      carateId: string;
      currencySymbol: string;
      currentPrice: number;
      date: string;
      hasUserUpdatedPrice: boolean;
      newPriceInitialValue: number;
      price: number;
      roomName: string;
      updateNewPrice: (
        value: number | null,
        carateId: string,
        roomName: string,
      ) => unknown;
    };
  };
}

export default class HotelPricingAssistantTableCellsTdPriceNew extends Component<HotelPricingAssistantTableCellsTdPriceNewSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get differenceValues() {
    if (this.newPrice && this.args.value.currentPrice) {
      const values = formatDifferenceBetweenRecommendationAndOwnRate(
        this.intl,
        this.newPrice,
        this.args.value.currentPrice,
        RECOMMENDATION_RATE_DIFFERENCE_FORMATS.RELATIVE,
        this.hotelAndMarketService,
      );
      return values;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    return this.args.value.newPriceInitialValue
      ? this.hotelAndMarketService.formatRate(
          this.args.value.newPriceInitialValue,
        )
      : this.intl.t('general.missing');
  }

  get hasErrors(): boolean {
    return (
      this.newPrice === null &&
      this.args.column.selectedRows.some(
        (row) => row.room?.roomName === this.args.row.room?.roomName,
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get newPrice() {
    if (this.args.value.hasUserUpdatedPrice) {
      return this.args.value.price;
    }

    return this.args.value.newPriceInitialValue;
  }

  get shouldDisplayDifference(): boolean {
    return this.differenceValues?.difference !== 0;
  }

  @action
  updatePrice(value: number | null): void {
    this.args.value.updateNewPrice(
      value,
      this.args.value['carateId'],
      this.args.value['roomName'],
    );
  }

  <template>
    {{#unless @row.isRoomCategory}}
      <div class="flex flex-row items-center">
        {{#if
          (and
            this.access.hasPricingAssistantEditRights
            this.access.hasPricingAssistantViewRights
          )
        }}
          <div class="price-input-container">
            <PrismNumberInput
              @autofocus={{@value.autofocus}}
              @hasErrors={{this.hasErrors}}
              @inlineLabel={{@value.currencySymbol}}
              @isSmall={{true}}
              @max={{NUMBER_INPUT_MAX_VALUE}}
              @min={{0}}
              @onValueChange={{this.updatePrice}}
              @removePadding={{true}}
              @value={{this.newPrice}}
            />
          </div>
        {{else}}
          {{this.formattedValue}}
        {{/if}}

        {{#if this.differenceValues}}
          {{#if this.shouldDisplayDifference}}
            <span
              {{assignTextSuccessCriticalClass
                this.differenceValues.difference
                0
              }}
              class="difference-value ml-200 truncate"
              title={{this.differenceValues.formattedDifference}}
            >
              {{this.differenceValues.formattedDifference}}
            </span>
          {{/if}}
        {{/if}}
      </div>
    {{/unless}}
  </template>
}
